import { useState } from 'react';
import PractitionerLeadDialog, { type PractitionerAction } from '@/pages/home/components/practitioner/PractitionerLeadDialog';

const offers = [
  {
    key: 'inscription',
    tag: 'Gratuit · Statut standard',
    tagColor: 'secondary',
    title: 'Inscrivez votre entreprise',
    desc: 'Vous n\'êtes pas encore sur l\'annuaire ? Créez votre fiche prospect en quelques secondes. Coordonnées visibles, sans agenda ni classement commercial.',
    features: [
      'Fiche visible dans l\'annuaire',
      'Coordonnées consultables par les patients',
      'Aucun engagement, aucune carte requise',
    ],
    cta: 'Créer ma fiche gratuite',
    icon: 'ri-add-circle-line',
  },
  {
    key: 'boost',
    tag: 'Formules AF · ARS · Premium',
    tagColor: 'primary',
    title: 'Boostez votre visibilité',
    desc: 'Gagnez en visibilité digitale grâce à votre présence sur le web et les réseaux sociaux, en plus de l\'agenda temps réel et de la prise de RDV en ligne.',
    features: [
      'Présence sur le web et les réseaux sociaux',
      'RDV en direct sur votre agenda Google/Outlook',
      'Rappels automatiques WhatsApp',
      'Fiche enrichie (photos, biographie, diplômes)',
      'Rapport mensuel détaillé',
    ],
    cta: 'Demander un devis',
    icon: 'ri-rocket-2-line',
    highlight: true,
  },
  {
    key: 'seo',
    tag: 'SEO · GEO · AEO',
    tagColor: 'accent',
    title: 'Faites-vous connaître',
    desc: 'Référencement classique (Google), référencement génératif (ChatGPT, Perplexity) et moteurs de réponse. Votre cabinet, partout où l\'on cherche des soins.',
    features: [
      'Optimisation SEO locale (Rabat, Casa…)',
      'Présence sur les IA génératives',
      'Contenus éditoriaux santé',
      'Suivi mensuel de votre visibilité',
    ],
    cta: 'Découvrir l\'offre',
    icon: 'ri-line-chart-line',
  },
];

export default function Practitioners() {
  const [openAction, setOpenAction] = useState<PractitionerAction | null>(null);

  return (
    <section id="praticiens" className="py-20 md:py-28 bg-background-50">
      <div className="px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="mb-14">
          <div className="max-w-2xl">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600 mb-3">
              Espace praticien
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight mb-4">
              Rejoignez la plateforme<br />
              <span className="italic text-primary-600">santé de Rabat.</span>
            </h2>
            <p className="text-foreground-600 text-base md:text-lg">
              Médecins, laboratoires, radiologues, pharmacies, paramédicaux : trois façons de rejoindre l'écosystème MEDICACLIC.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {offers.map((o) => (
            <div
              key={o.key}
              className={`relative flex flex-col p-7 rounded-2xl transition-all cursor-pointer ${
                o.highlight
                  ? 'bg-secondary-900 border-2 border-primary-500 text-background-50'
                  : 'bg-background-100 border border-background-200 hover:border-primary-300'
              }`}
            >
              {o.highlight && (
                <span className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-[0.65rem] font-bold uppercase tracking-wider">
                  Le plus choisi
                </span>
              )}

              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 ${
                o.highlight ? 'bg-primary-500' :
                o.tagColor === 'accent' ? 'bg-accent-100' :
                o.tagColor === 'secondary' ? 'bg-secondary-100' : 'bg-primary-100'
              }`}>
                <i className={`${o.icon} text-xl w-5 h-5 flex items-center justify-center ${
                  o.highlight ? 'text-background-50' :
                  o.tagColor === 'accent' ? 'text-accent-700' :
                  o.tagColor === 'secondary' ? 'text-secondary-700' : 'text-primary-700'
                }`} />
              </div>

              <span className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
                o.highlight ? 'text-primary-300' :
                o.tagColor === 'accent' ? 'text-accent-700' :
                o.tagColor === 'secondary' ? 'text-secondary-700' : 'text-primary-700'
              }`}>
                {o.tag}
              </span>

              <h3 className={`font-heading text-2xl font-semibold mb-3 ${o.highlight ? 'text-background-50' : 'text-foreground-950'}`}>
                {o.title}
              </h3>

              <p className={`text-sm leading-relaxed mb-5 ${o.highlight ? 'text-background-200' : 'text-foreground-600'}`}>
                {o.desc}
              </p>

              <ul className="space-y-2.5 mb-7 flex-1">
                {o.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2 text-sm ${o.highlight ? 'text-background-100' : 'text-foreground-700'}`}>
                    <i className={`ri-check-line text-base w-4 h-4 flex items-center justify-center mt-0.5 ${
                      o.highlight ? 'text-primary-300' : 'text-primary-600'
                    }`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => setOpenAction(o.key as PractitionerAction)}
                className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                o.highlight
                  ? 'bg-primary-500 hover:bg-primary-600 text-background-50'
                  : 'bg-foreground-950 hover:bg-foreground-800 text-background-50'
              }`}>
                {o.cta}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <PractitionerLeadDialog action={openAction} onClose={() => setOpenAction(null)} />
    </section>
  );
}